/* global grecaptcha */
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import TextComponent from 'shared/components/TextComponent';
import TextAreaComponent from 'shared/components/TextAreaComponent';
import validateEmail from 'shared/utils/validateEmail';

const EmailQuoteModal = (props) => {
  const {
    quote,
    show,
    onSend,
    onHide,
    title,
    notePlaceHolder,
    sendLabel,
    cancelLabel,
  } = props;

  const [email, setEmail] = useState();
  const [note, setNote] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [recaptchaCompleted, setRecaptchaCompleted] = useState(false);
  const recaptchaWidgetId = useRef(null);
  const recaptchaElementId = `recaptcha-email-${quote?.number || quote?.id || 'default'}`;

  useEffect(() => {
    // Render reCAPTCHA when modal opens and avoid duplicate renders
    if (!show) return undefined;

    const tryRender = () => {
      if (recaptchaWidgetId.current !== null) return;
      if (typeof grecaptcha === 'undefined' || !grecaptcha.ready) {
        setTimeout(tryRender, 100);
        return;
      }

      grecaptcha.ready(() => {
        // Element might not exist yet
        const el = document.getElementById(recaptchaElementId);
        if (!el) {
          setTimeout(tryRender, 100);
          return;
        }

        recaptchaWidgetId.current = grecaptcha.render(el, {
          sitekey: Spree.recaptchaSiteKey,
          callback: () => setRecaptchaCompleted(true),
        });
      });
    };

    tryRender();

    return undefined;
  }, [show, recaptchaElementId]);

  useEffect(() => {
    // Reset when modal closes
    if (!show && recaptchaWidgetId.current !== null && typeof grecaptcha !== 'undefined') {
      grecaptcha.reset(recaptchaWidgetId.current);
      recaptchaWidgetId.current = null;
      setRecaptchaCompleted(false);
      setEmailError(null);
    }
  }, [show]);

  const onConfirm = () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    let recaptchaToken = null;
    if (recaptchaWidgetId.current !== null && typeof grecaptcha !== 'undefined' && grecaptcha.getResponse) {
      recaptchaToken = grecaptcha.getResponse(recaptchaWidgetId.current);
    }

    const data = {
      'g-recaptcha-response': recaptchaToken,
      product_quote_number: quote.number,
      product_quote_email_request: {
        product_quote_id: quote.id,
        email,
        note,
      },
    };
    onSend(data);
    setNote('');

    if (recaptchaWidgetId.current !== null && typeof grecaptcha !== 'undefined') {
      grecaptcha.reset(recaptchaWidgetId.current);
      recaptchaWidgetId.current = null;
    }

    setRecaptchaCompleted(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <TextComponent
                label="To"
                placeholder="Enter email address"
                name="email"
                groupName={null}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                required />
              {emailError && (
                <div className="text-danger mt-1" role="alert">
                  {emailError}
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <TextAreaComponent
                label="Add a note"
                placeholder={notePlaceHolder}
                value={note}
                name="note"
                groupName={null}
                type="text"
                onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 d-flex justify-content-center mt-3">
              <div id={recaptchaElementId} />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {cancelLabel}
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={!recaptchaCompleted}>
          {sendLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EmailQuoteModal.defaultProps = {
  show: false,
  title: 'Email Quote',
  notePlaceHolder: 'I thought you might be interested in this page from Pentalogix.',
  sendLabel: 'Send',
  cancelLabel: 'Cancel',
};

export default EmailQuoteModal;
